import {useState} from "react";
import {Table, Tag, Alert} from "antd";
import dayjs from "dayjs";
import {PageHeader, Loading, FontAwesomeIcon, Button} from "~/components";
import {InputField, SelectField} from "~/components/Forms";
import {useGetMembersQuery} from "~/reduxs/api/memberApiSlice";
import {useGetAdminPlansQuery} from "~/reduxs/api/planApiSlice";
import {nf, compact, planColor, statusMeta, STATUS_OPTIONS} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const PAGE_SIZE = 20;

function MemberList() {
	const [kwInput, setKwInput] = useState(''); // ô nhập (áp dụng khi Enter)
	const [keyword, setKeyword] = useState(''); // từ khoá đã áp dụng
	const [status, setStatus] = useState('');
	const [plan, setPlan] = useState('');
	const [page, setPage] = useState(1);

	// Đổi bộ lọc → về trang 1.
	const applyKeyword = () => { setKeyword(kwInput.trim()); setPage(1); };
	const onStatus = (v) => { setStatus(v || ''); setPage(1); };
	const onPlan = (v) => { setPlan(v || ''); setPage(1); };

	const {data, isLoading, isError} = useGetMembersQuery({keyword, status, plan, page, page_size: PAGE_SIZE});
	const {data: plans = []} = useGetAdminPlansQuery();

	const members = data?.items || [];
	const total = data?.total || 0;

	const columns = [
		{
			title: 'Thành viên',
			key: 'member',
			render: (_, r) => (
				<div className={cn('member-cell')}>
					<div className={cn('name')}>{r.fullname}</div>
					<div className={cn('sub')}>@{r.username}{r.email ? ` · ${r.email}` : ''}</div>
				</div>
			),
		},
		{
			title: 'Gói',
			key: 'plan',
			render: (_, r) => <Tag color={planColor(r.plan?.slug)}>{r.plan?.name}</Tag>,
		},
		{
			title: 'Trạng thái',
			key: 'status',
			render: (_, r) => {
				const m = statusMeta(r.status);
				return <Tag color={m.color}>{m.label}</Tag>;
			},
		},
		// Cột phụ — ẩn trên mobile, bảng vẫn cuộn ngang được nhờ scroll x
		{title: 'Dự án', dataIndex: 'projects', key: 'projects', align: 'center', responsive: ['md']},
		{
			title: 'AI Token',
			key: 'tokens',
			align: 'center',
			responsive: ['md'],
			render: (_, r) => `${compact(r.ai_tokens?.used)} / ${compact(r.ai_tokens?.limit)}`,
		},
		{
			title: 'Đăng nhập gần nhất',
			key: 'last_login',
			responsive: ['md'],
			render: (_, r) => (r.last_login ? dayjs(r.last_login).format('HH:mm DD/MM/YYYY') : '—'),
		},
		{
			title: '',
			key: 'action',
			align: 'right',
			render: (_, r) => (
				<Button to={`/admin/members/${r.id}`} white small
					leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />}>
					Chi tiết
				</Button>
			),
		},
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-users"
				title="Quản lý thành viên"
				subtitle="Xem thông tin, gói dịch vụ, hạn mức và trạng thái của thành viên."
			/>

			<div className={cn('filters') + ' form'}>
				<div style={{width: 320}}>
					<InputField
						placeholder="Tìm theo tên, username, email, SĐT… (Enter)"
						value={kwInput}
						allowClear
						onChange={(e) => setKwInput(e.target.value)}
						onPressEnter={applyKeyword}
					/>
				</div>
				<div style={{width: 190}}>
					<SelectField
						value={status}
						onChange={onStatus}
						options={[{value: '', label: 'Tất cả trạng thái'}, ...STATUS_OPTIONS]}
					/>
				</div>
				<div style={{width: 190}}>
					<SelectField
						value={plan}
						onChange={onPlan}
						options={[{value: '', label: 'Tất cả gói'}, ...plans.map((p) => ({value: p.slug, label: p.name}))]}
					/>
				</div>
			</div>

			{isError ? (
				<Alert type="error" showIcon message="Không tải được danh sách thành viên." />
			) : isLoading ? (
				<Loading />
			) : (
				<Table
					columns={columns}
					dataSource={members}
					rowKey="id"
					pagination={{
						current: page,
						pageSize: PAGE_SIZE,
						total,
						onChange: setPage,
						showSizeChanger: false,
						showTotal: (t) => `${nf(t)} thành viên`,
					}}
					scroll={{x: 'max-content'}}
				/>
			)}
		</div>
	);
}

export default MemberList;
